import { AppState } from "@-/com.npmjs.react-native";
import { useEffect, useState } from "react";

import { localDayKey, startOfNextLocalDay } from "#local-day";

// Fired a beat after the computed midnight so a timer that lands a few milliseconds early (timer
// coalescing, a clock that was nudged backwards) still reads the NEW day rather than re-arming
// for the old one.
const MIDNIGHT_TICK_SLACK_MS = 250;

/**
 * The current local calendar day as `YYYY-MM-DD`, re-rendering when it changes.
 *
 * Two things move it forward: a timer armed for the next local midnight, and the app coming back
 * to the foreground (a phone left on the drill overnight, or backgrounded across the boundary,
 * never runs the timer on time). Both paths re-derive the key from the wall clock rather than
 * assuming a day has passed, so neither can skip or double-count a day.
 *
 * Meant for query keys that must roll over at the day boundary — see `localDayKey`.
 */
export function useLocalDayKey(): string {
  const [dayKey, setDayKey] = useState(() => localDayKey(new Date()));

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const refresh = () => {
      const now = new Date();
      const nextKey = localDayKey(now);
      setDayKey((current) => (current === nextKey ? current : nextKey));
      clearTimeout(timeout);
      const msUntilNextDay = startOfNextLocalDay(now).getTime() - now.getTime();
      timeout = setTimeout(refresh, Math.max(1000, msUntilNextDay + MIDNIGHT_TICK_SLACK_MS));
    };

    refresh();
    const subscription = AppState.addEventListener("change", (state) => {
      if (state === "active") {
        refresh();
      }
    });
    return () => {
      clearTimeout(timeout);
      subscription.remove();
    };
  }, []);

  return dayKey;
}
